import { yupResolver } from "@hookform/resolvers/yup";
import type { FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useModalStore } from "store/modal/hooks/useModalStore";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { AppModal } from "@krea/shared/design-system/modal";
import {
  RadioGroup,
  RadioGroupItem,
} from "@krea/shared/design-system/RadioGroup";
import { Textarea } from "@krea/shared/design-system/Textarea";
import { cn } from "@krea/shared/utils";

import type { RejectedByLenderReasonEnum } from "@/features/common/enums";
import { useRejectApplicationMutation } from "@/features/lender-applications/hooks/data/usereject-application.mutation";

const REJECT_VALUES = [
  { value: "LENDER_DECISION", label: "Rejected by credit" },
  { value: "CUSTOMER_REASON", label: "Rejected by customer" },
];

const schema = yup.object().shape({
  rejectionReason: yup
    .string()
    .required("Rejection reason is required.")
    .oneOf(REJECT_VALUES.map(({ value }) => value)),
  explanation: yup.string().required("Explanation is required."),
});

export const RejectAfterWinningModal = () => {
  const modalParams = useModalStore((state) =>
    state.modalParams?.type === "rejectAfterWinning"
      ? state.modalParams.params
      : null,
  );

  const {
    loading: isModalLoading,
    closeModal,
    setModalLoading,
  } = useModalStore((state) => ({
    loading: state.loading,
    closeModal: state.closeModal,
    setModalLoading: state.setModalLoading,
  }));

  const { mutate: rejectApplication } = useRejectApplicationMutation();
  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      rejectionReason: REJECT_VALUES[0]?.value,
      explanation: undefined,
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    if (!modalParams) return;
    setModalLoading(true);
    form.handleSubmit((data) => {
      rejectApplication(
        {
          data: {
            rejectedByLenderDescription: data.explanation,
            rejectedByLenderReason:
              data.rejectionReason as RejectedByLenderReasonEnum,
          },
          lenderCompanyId: modalParams.lenderCompanyId,
          lenderApplicationId: modalParams.lenderApplicationId,
        },
        {
          onSuccess: () => {
            setModalLoading(false);
            closeModal();
          },
        },
      );
    })(e);
  };

  const onClose = () => {
    closeModal();
  };

  if (!modalParams) return null;

  return (
    <AppModal
      open={true}
      setOpen={(open) => {
        if (!open) onClose();
      }}
      onClose={onClose}
      title={`Reject application #${modalParams.lenderApplicationId} after winning`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            form="rejectApplicationForm"
            type="submit"
            disabled={isModalLoading || !form.formState.isValid}
          >
            Reject after winning
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} id="rejectApplicationForm">
        <ControlledWrapper
          control={form.control}
          name="rejectionReason"
          label={<span className="font-semibold">Select Reason</span>}
          render={({ field }) => (
            <RadioGroup
              onValueChange={field.onChange}
              defaultValue={field.value}
            >
              {REJECT_VALUES.map(({ value, label }) => (
                <div
                  key={value + label}
                  className={cn(
                    "flex items-center gap-2 h-12 px-3 rounded-md border cursor-pointer",
                    {
                      "bg-primary-150 border-transparent":
                        field.value === value,
                      "text-primary-400": field.value !== value,
                    },
                  )}
                >
                  <RadioGroupItem value={value} />
                  <span className="font-normal">{label}</span>
                </div>
              ))}
            </RadioGroup>
          )}
        />

        <ControlledWrapper
          control={form.control}
          name="explanation"
          label={<span className="font-semibold">Explanation</span>}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder="Enter explanation"
              required={true}
            />
          )}
        />
      </form>
    </AppModal>
  );
};
